import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import type { Silhouette as SilType } from "../shared/styles";

// ---------------- 图标（线性，1.6px） ----------------

const PATHS: Record<string, string> = {
  back: "M15 5l-7 7 7 7",
  plus: "M12 5v14M5 12h14",
  camera:
    "M4 8.5A2.5 2.5 0 0 1 6.5 6h1.2l1.3-2h6l1.3 2h1.2A2.5 2.5 0 0 1 20 8.5v9a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5zM12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z",
  image:
    "M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5zM4 16l4.5-4.5 4 4 2.5-2.5L20 18M15.5 9.5h.01",
  heart: "M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z",
  download: "M12 4v11m0 0l-4.5-4.5M12 15l4.5-4.5M5 19.5h14",
  more: "M6 12h.01M12 12h.01M18 12h.01",
  settings:
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 13.5l1.3 1-1.8 3.1-1.6-.5a7 7 0 0 1-1.7 1l-.3 1.7h-3.6l-.3-1.7a7 7 0 0 1-1.7-1l-1.6.5-1.8-3.1 1.3-1a7 7 0 0 1 0-2l-1.3-1 1.8-3.1 1.6.5a7 7 0 0 1 1.7-1l.3-1.7h3.6l.3 1.7a7 7 0 0 1 1.7 1l1.6-.5 1.8 3.1-1.3 1a7 7 0 0 1 0 2z",
  close: "M6 6l12 12M18 6L6 18",
  sparkle:
    "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z",
  lock: "M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6z",
  compare: "M12 4v16M8 7H5v10h3M16 7h3v10h-3",
  trash: "M5 7h14M10 7V5h4v2M7 7l1 13h8l1-13",
  refresh: "M19 12a7 7 0 1 1-2.1-5M19 4v4h-4",
  check: "M5 12.5l4.5 4.5L19 7.5",
  wand: "M5 19L15 9M14 4v2M19 9h-2M17.5 5.5l-1.4 1.4M11 5l.5 1.5M18.5 12l-1.5.5",
  grid: "M5 5h5.5v5.5H5zM13.5 5H19v5.5h-5.5zM5 13.5h5.5V19H5zM13.5 13.5H19V19h-5.5z",
};

export function Icon({
  name,
  size = 22,
  filled = false,
}: {
  name: keyof typeof PATHS | string;
  size?: number;
  filled?: boolean;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      stroke-width="1.7"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[name] ?? ""} />
    </svg>
  );
}

// ---------------- 发型剪影（未生成时的占位） ----------------

const HAIR: Record<SilType, string> = {
  buzz: "M17 27.5c0-8 6-12.5 13-12.5s13 4.5 13 12.5c-1.5-4.5-6.5-8-13-8s-11.5 3.5-13 8z",
  crop: "M16.5 29c-.5-9 5.5-15 13.5-15s14 6 13.5 15c-1-3-2.5-5-4.5-6-2 2-6 2.5-9.5 1.5-3.5 1-7 .5-9-1.5-2 1-3.5 3-4 6z",
  swept:
    "M16.5 30c-1.5-10 4.5-17 14-17 5 0 9 1.5 11.5 5 2 3 2 7.5 1.5 12-1-3.5-3-6-5.5-7.5-5 .5-10-1-13-3.5-3 2.5-6 6-8.5 11z",
  pixie: "M16 34c-2-12 4-20 14-20s16 7 14 19c-.5-4-2-7-4-9-4 1-10 0-13-3-2.5 3-6 5-9 5-1 2.5-1.8 5-2 8z",
  bob: "M14 44c-2-4-2.5-10-1.5-16 1.5-9 8.5-14 17.5-14s16 5 17.5 14c1 6 .5 12-1.5 16h-5c1-5 1-10 0-15-3.5 1-9 1-13-2-3 3-6.5 4.5-9 4.5-.8 4-.8 8.5.3 12.5z",
  lob: "M13 54c-2-8-2.5-18-1-27 1.5-8.5 8.5-13 18-13s16.5 4.5 18 13c1.5 9 1 19-1 27h-5.5c1.5-8 2-17 .5-26-4 .5-9-1-12-4-3.5 3-7.5 4-10.5 4-1.5 9-1 18 .5 26z",
  long: "M12 66c-3-12-3.5-26-1.5-38 1.5-9 9-14 19.5-14s18 5 19.5 14c2 12 1.5 26-1.5 38h-6c2-12 2.5-26 .5-38-4 .5-9-1.5-12.5-4.5-3.5 3-8 4.5-12 4.5-2 12-1.5 26 .5 38z",
  curly:
    "M13 48c-3-2-3.5-6-1.5-8-2.5-2.5-2-6.5.5-8-1.5-3.5.5-7 3.5-8 0-4 3.5-7 7-6.5 2-3 6.5-4 9.5-2 3.5-2 8-1 9.5 2 3.5-.5 7 2.5 7 6.5 3 1 5 4.5 3.5 8 2.5 1.5 3 5.5.5 8 2 2 1.5 6-1.5 8h-5c1.5-6 1-12-1-17-3.5 0-8.5-1.5-11.5-4-3 2.5-7.5 4-11 4-2 5-2.5 11-1 17z",
  bun: "M17 28c0-8 6-12.5 13-12.5s13 4.5 13 12.5c-1.5-4.5-6.5-8-13-8s-11.5 3.5-13 8zM30 13.5a6 6 0 1 0 0-12 6 6 0 0 0 0 12z",
  wolf: "M13 50c1-4 1.5-7 1-10-2.5-9 3-19 11-21 7-2 16 0 19 7 2 5 2 11 .5 15 1 3 2.5 6 3.5 9l-5-2c-.5-7-1-13-3-17-4 1-9 0-12.5-3-3 3-6.5 4.5-9.5 4.5-1 5-1.5 10-.5 14z",
};

export function Silhouette({
  type,
  hue = "var(--sage)",
  gender = "female",
}: {
  type: SilType;
  hue?: string;
  gender?: "female" | "male";
}) {
  const behind = type === "long" || type === "lob" || type === "bob" || type === "curly" || type === "wolf";
  const hair = <path d={HAIR[type]} fill={hue} opacity="0.9" />;
  const male = gender === "male";
  return (
    <svg viewBox="0 0 60 72" class="sil" aria-hidden="true">
      {behind && hair}
      {male ? (
        <path d="M4 72c0-12 11-19.5 26-19.5S56 60 56 72z" fill="var(--sil-body)" />
      ) : (
        <path d="M9 72c0-12 9-18.5 21-18.5S51 60 51 72z" fill="var(--sil-body)" />
      )}
      <rect x={male ? 24 : 25.5} y="40" width={male ? 12 : 9} height="15" rx="4" fill="var(--sil-skin)" />
      {male ? (
        <path d="M18 27c0-8 5.5-13 12-13s12 5 12 13v5c0 7-5 12.5-12 12.5S18 39 18 32z" fill="var(--sil-skin)" />
      ) : (
        <ellipse cx="30" cy="30" rx="11.5" ry="13.5" fill="var(--sil-skin)" />
      )}
      {!behind && hair}
      {behind && (
        <path
          d="M17.8 29c-.3-8 5.2-13.2 12.2-13.2S42.5 21 42.2 29c-2.5-3.5-6.5-5.6-12.2-5.6S20.3 25.5 17.8 29z"
          fill={hue}
          opacity="0.9"
        />
      )}
    </svg>
  );
}

// ---------------- 进度环 ----------------

export function Ring({ value, size = 64, stroke = 5 }: { value: number; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} class="ring" viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--ring-track)" stroke-width={stroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="var(--ring)"
        stroke-width={stroke}
        fill="none"
        stroke-linecap="round"
        stroke-dasharray={c}
        stroke-dashoffset={c * (1 - Math.max(0, Math.min(100, value)) / 100)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

// ---------------- 底部抽屉 ----------------

export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ComponentChildren;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div class="sheet-wrap" onClick={onClose}>
      <div class="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div class="sheet-grip" />
        {title && (
          <div class="sheet-head">
            <h2>{title}</h2>
            <button class="icon-btn" onClick={onClose} aria-label="关闭">
              <Icon name="close" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

// ---------------- Toast ----------------

type ToastItem = { id: number; text: string; tone: "info" | "error" };
let toastId = 0;
const toastSubs = new Set<(t: ToastItem[]) => void>();
let toasts: ToastItem[] = [];

export function toast(text: string, tone: "info" | "error" = "info") {
  const item = { id: ++toastId, text, tone };
  toasts = [...toasts, item];
  for (const s of toastSubs) s(toasts);
  setTimeout(
    () => {
      toasts = toasts.filter((t) => t.id !== item.id);
      for (const s of toastSubs) s(toasts);
    },
    tone === "error" ? 4200 : 2400,
  );
}

export function Toasts() {
  const [list, setList] = useState<ToastItem[]>(toasts);
  useEffect(() => {
    toastSubs.add(setList);
    return () => {
      toastSubs.delete(setList);
    };
  }, []);
  return (
    <div class="toasts" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} class={`toast ${t.tone}`}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

export function fmtBytes(n: number): string {
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
